/*eslint-disable*/

import React, { useEffect, useMemo, useState } from 'react'
import { Button, Input, Loading, Popup, SelectInput } from 'src/components'
import { useStyles } from './styles'
import { useForm, FormProvider } from 'react-hook-form'
import { CreateAnimalForm, createAnimalSchema } from 'src/@types/validations/Animal'
import { yupResolver } from '@hookform/resolvers/yup'
import { AnimalCompleteRegistration } from 'src/pages/Protected/CreateAnimal/components/AnimalCompleteRegistration'
import { useGetBreedList } from 'src/hooks/api/breed'
import { useGetLotList } from 'src/hooks/api/lot'
import { useGetPastureList } from 'src/hooks/api/pasture'
import { useGetCoatList } from 'src/hooks/api/coat'
import { useGetBloodGradeList } from 'src/hooks/api/animal'
import { birthTypeOptions } from 'src/constants/birthType'
import { SavedAnimal } from '../SavedAnimal'
import { DiagnosisBadge } from '../DiagnosisBadge'
import ArrowBack from '@material-ui/icons/ArrowBack'
import { formatDateToInputValue } from 'src/utils'
import { calculateGestationTime } from '../../utils/functions'
import { fileToBase64 } from 'src/utils/file'
import { useGetBirthCertificateList } from 'src/hooks/api/birth-certificate'
import { useAuth } from 'src/contexts/auth'
import { EarringDto } from 'src/dto/birth-certificate.dto'
import { useAvailableEarrings } from 'src/hooks/useAvailableEarrings'

export type DiagnosisStatus = 'prenha' | 'vazia'

type ViewMode = 'diagnosis' | 'animalForm'

interface DiagnosisModalProps {
  initView?: ViewMode
  isOpen: boolean
  onClose: () => void
  motherAnimalId: string
  motherAnimalName: string
  fatherAnimalId: string
  diagnosisDate?: string
  reproductionType?: string
  reproductionDate?: string
  onSaveAll: (births: (CreateAnimalForm & { earringSelected?: EarringDto })[], birthInfo: BirthInfo) => void
  existingBirths?: (CreateAnimalForm & { earringSelected?: EarringDto })[]
}

export interface BirthInfo {
  dataParto: string
  tempoGestacao: number | null
  tipoParto: 'normal' | 'cesariana'
}

export const DiagnosisModal: React.FC<DiagnosisModalProps> = ({
  initView = 'diagnosis',
  isOpen,
  onClose,
  motherAnimalId,
  motherAnimalName,
  fatherAnimalId,
  diagnosisDate,
  reproductionType = 'Monta Natural',
  reproductionDate,
  onSaveAll,
  existingBirths = []
}) => {
  const { username } = useAuth()
  const style = useStyles()
  const [animalImage, setAnimalImage] = useState<File | null>(null)
  const [savedBirths, setSavedBirths] = useState<(CreateAnimalForm & { earringSelected?: EarringDto })[]>([])
  const [viewMode, setViewMode] = useState<ViewMode>(initView)

  const { data: breedList = [], isLoading: isLoadingBreedList } = useGetBreedList()
  const { data: lotsList = [], isLoading: isLoadingLotList } = useGetLotList()
  const { data: pastureList = [], isLoading: isLoadingPastureList } = useGetPastureList()
  const { data: coatList = [], isLoading: isLoadingCoatList } = useGetCoatList()
  const { data: bloodGradeList = [], isLoading: isLoadingBloodGradeList } = useGetBloodGradeList()
  const { data: birthCertificateList, isLoading: isBirthCertificateListLoading } = useGetBirthCertificateList({
    userFilter: username
  })

  const isLoadingFormOptions =
    isLoadingBreedList ||
    isLoadingLotList ||
    isLoadingPastureList ||
    isLoadingCoatList ||
    isLoadingBloodGradeList ||
    isBirthCertificateListLoading

  const defaultBirthInfoValues: BirthInfo = {
    dataParto: diagnosisDate || new Date().toISOString(),
    tempoGestacao: null,
    tipoParto: 'normal'
  }

  const defaultFormValues = {
    sexo: 'F',
    tipoAnimal: 'nasceu_fazenda',
    idAnimalMae: motherAnimalId || null,
    idAnimalPai: fatherAnimalId || null,
    dataNascimento: defaultBirthInfoValues.dataParto || ''
  }

  const [birthInfo, setBirthInfo] = useState<BirthInfo>(defaultBirthInfoValues)

  const formMethods = useForm<CreateAnimalForm & { earringSelected?: EarringDto }>({
    resolver: yupResolver(createAnimalSchema),
    defaultValues: defaultFormValues
  })

  const { watch, setValue, reset, handleSubmit } = formMethods

  useEffect(() => {
    if (isOpen) {
      setSavedBirths(existingBirths)
      setViewMode(initView)
      const gestationTime = calculateGestationTime(diagnosisDate, reproductionDate)

      setBirthInfo({
        ...defaultBirthInfoValues,
        tempoGestacao: gestationTime
      })

      reset(defaultFormValues)
    }
  }, [isOpen, reset, motherAnimalId, fatherAnimalId, diagnosisDate, reproductionDate, existingBirths, initView])

  const excludeIds = useMemo(() => {
    return savedBirths.map((b) => b.earringSelected?.idBrinco).filter(Boolean) as string[]
  }, [savedBirths])

  const { availableEarrings: availableEarringsFiltered, selectFirstEarring } = useAvailableEarrings({
    birthCertificateList,
    excludeIds
  })

  const selected = watch('earringSelected')
  useEffect(() => {
    if (selected) return
    const earring = selectFirstEarring()
    if (earring) {
      setValue('earringSelected', earring)
      setValue('rgn', earring.numeroSufixo)
      setValue('cdn', earring.numeroCompleto)
    }
  }, [availableEarringsFiltered, selected, selectFirstEarring, setValue])

  const confirmAddBirth = async (value: CreateAnimalForm & { earringSelected?: EarringDto }) => {
    // Converter imagem File para base64 antes de adicionar
    let imageBase64: string | null = null
    if (animalImage) {
      try {
        imageBase64 = await fileToBase64(animalImage)
      } catch (error) {
        console.error('Erro ao converter imagem para base64:', error)
      }
    }

    const newBirth = {
      ...value,
      tipoAnimal: 'nasceu_fazenda',
      idAnimalMae: motherAnimalId || null,
      idAnimalPai: fatherAnimalId || null,
      earringSelected: value?.earringSelected,
      imagem: imageBase64
    }

    // Se initView é 'animalForm', salvar direto sem passar pela view de diagnóstico
    if (initView === 'animalForm') {
      const updatedBirths = [...savedBirths, newBirth]
      const updatedBirthInfo = {
        ...birthInfo,
        tempoGestacao: calculateGestationTime(diagnosisDate, reproductionDate)
      }
      onSaveAll(updatedBirths, updatedBirthInfo)
      reset(defaultFormValues)
      setAnimalImage(null)
      onClose()
    } else {
      // Comportamento normal: adiciona ao array e volta para view de diagnóstico
      setSavedBirths((prev) => [...prev, newBirth])
      setViewMode('diagnosis')
      reset(defaultFormValues)
      setAnimalImage(null)
    }
  }

  const handleAddBirth = handleSubmit(async (value) => {
    await confirmAddBirth(value)
  })

  const handleSaveAll = () => {
    if (savedBirths.length === 0) {
      return
    }
    const updatedBirthInfo = {
      ...birthInfo,
      tempoGestacao: calculateGestationTime(diagnosisDate, reproductionDate)
    }

    onSaveAll(savedBirths, updatedBirthInfo)
    onClose()
  }

  const handleRemoveBirth = (index: number) => {
    setSavedBirths((prev) => prev.filter((_, i) => i !== index))
  }

  const handleCloseModal = () => {
    setSavedBirths([])
    setViewMode(initView)
    onClose()
  }

  const handleBackToDiagnosis = () => {
    if (!initView || initView !== 'animalForm') {
      setViewMode('diagnosis')
    } else {
      onClose()
    }
    reset({
      ...defaultFormValues,
      dataNascimento: birthInfo.dataParto || ''
    })
  }

  if (!isOpen) {
    return null
  }

  const pendingDiagnosisBadge =
    viewMode === 'diagnosis' ? (
      <DiagnosisBadge diagnosis={null} item={{ dataParto: diagnosisDate || '' } as any} />
    ) : null

  const renderDiagnosisView = () => {
    switch (viewMode) {
      case 'diagnosis':
        return (
          <>
            <div className={style.contentContainer}>
              <div className={style.infoSection}>
                <SelectInput
                  label='Vaca'
                  value={motherAnimalId}
                  options={[{ id: motherAnimalId, value: motherAnimalName }]}
                  selectBy='id'
                  showBy={['value']}
                  placeholder='Vaca selecionada'
                  onChange={() => {}}
                  disabled
                />
                <Input
                  type='date'
                  max='2999-12-31'
                  label='Data'
                  value={formatDateToInputValue(watch('dataNascimento') || '')}
                  onChange={(value) => {
                    const date = new Date(value).toISOString()
                    setBirthInfo((prev) => ({ ...prev, dataParto: date }))
                    setValue('dataNascimento', date, { shouldDirty: true })
                  }}
                  disabled
                />
              </div>
              <h3 className={style.sectionTitle}>Detalhes da Reprodução</h3>
              <div className={style.section}>
                <div className={style.reproductionTypeInfo}>
                  <h4 className={style.sectionTitle}>{reproductionType}</h4>
                </div>

                <div className={style.formRow}>
                  <div className={style.formControl}>
                    <Input
                      type='date'
                      max='2999-12-31'
                      label='Data da Cobertura'
                      value={formatDateToInputValue(reproductionDate || '')}
                      onChange={() => {}}
                      disabled
                    />
                  </div>

                  <div className={style.formControl}>
                    <Input
                      type='number'
                      label='Tempo de Gestação (dias)'
                      value={calculateGestationTime()?.toString() || '0'}
                      onChange={() => {}}
                      disabled
                    />
                  </div>
                </div>
              </div>

              {/* Lista de Crias Adicionadas */}
              <div>
                <h3 className={style.sectionTitle}>Crias</h3>
                {savedBirths.length > 0 && (
                  <div className={style.savedBirthsContainer}>
                    {savedBirths.map((birth, index) => (
                      <SavedAnimal
                        key={`birth-${index}`}
                        savedBirthAnimal={birth}
                        index={index}
                        onRemove={() => handleRemoveBirth(index)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Tipo de Parto */}
              <div className={style.formControl}>
                <SelectInput
                  label='Tipo de Parto'
                  value={birthInfo.tipoParto}
                  options={birthTypeOptions.filter((opt) => opt.id === 'normal' || opt.id === 'cesariana')}
                  selectBy='id'
                  showBy={['value']}
                  placeholder='Selecione o tipo de parto'
                  onChange={(option: any) => {
                    setBirthInfo((prev) => ({ ...prev, tipoParto: option.id }))
                  }}
                />
              </div>

              {/* Botão Adicionar Cria */}
              <div className={style.addBirthButtonContainer}>
                <Button
                  type='button'
                  className={style.buttonWrapper}
                  onClick={() => setViewMode('animalForm')}
                  outlined
                >
                  Adicionar Cria
                </Button>
              </div>
            </div>

            {/* Ações da View de Diagnóstico */}
            <div className={style.actionsContainer}>
              <Button className={`${style.buttonWrapper} ${style.buttonOutlined}`} onClick={handleCloseModal} outlined>
                Cancelar
              </Button>
              <Button className={style.buttonWrapper} onClick={handleSaveAll} disabled={savedBirths.length === 0}>
                Salvar Nascimentos
              </Button>
            </div>
          </>
        )

      default:
        return (
          <>
            {initView === 'diagnosis' && (
              <div className={style.backButtonContainer}>
                <Button
                  type='button'
                  className={`${style.buttonWrapper} ${style.buttonOutlined}`}
                  onClick={handleBackToDiagnosis}
                  outlined
                >
                  <ArrowBack className={style.backIcon} />
                  Voltar
                </Button>
              </div>
            )}
            <div className={style.formContentWrapper}>
              <FormProvider {...formMethods}>
                <form onSubmit={handleAddBirth}>
                  {isLoadingFormOptions ? (
                    <Loading />
                  ) : (
                    <AnimalCompleteRegistration
                      breedList={breedList}
                      lotsList={lotsList}
                      pastureList={pastureList}
                      coatList={coatList}
                      bloodGradeList={bloodGradeList}
                      setAnimalImage={setAnimalImage}
                      handleSaveImage={async () => {}}
                      isDiagnosticRestiger={true}
                    />
                  )}
                </form>
              </FormProvider>
            </div>

            <div className={style.actionsContainer}>
              <Button
                className={`${style.buttonWrapper} ${style.buttonOutlined}`}
                onClick={handleBackToDiagnosis}
                outlined
              >
                Cancelar
              </Button>
              <Button
                type='button'
                className={style.buttonWrapper}
                disabled={isLoadingFormOptions}
                onClick={handleAddBirth}
              >
                Salvar Cria
              </Button>
            </div>
          </>
        )
    }
  }

  return (
    <Popup
      title={viewMode === 'diagnosis' ? 'Cadastrar nascimento' : 'Dados da Cria'}
      className={`${style.diagnosisPopup} ${viewMode === 'animalForm' ? style.diagnosisPopupAnimalForm : ''}`}
      onClose={handleCloseModal}
      headerBadge={pendingDiagnosisBadge}
      closeOnClickOutside={false}
    >
      <div className={style.formContainer}>{renderDiagnosisView()}</div>
    </Popup>
  )
}
